import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  CheckCircle2,
  Globe,
  Key,
  MapPin,
  Shield,
  Star,
  ChevronDown,
} from 'lucide-react';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import PropertyCardSkeleton from '../components/PropertyCardSkeleton';
import AnimatedCounter from '../components/AnimatedCounter';
import LivingRoomHoraScene from '../components/LivingRoomHoraScene';
import RentalNetworkHero from '../components/home/RentalNetworkHero';
import SEOHead, { orgJsonLd, websiteJsonLd } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import {
  fadeUp,
  fadeUpSmall,
  staggerParent,
  inViewOnce,
  ease,
} from '../lib/animations';
import { DEMO_PROPERTIES, DEMO_CITIES } from '../lib/demoData';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function Landing() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [featuredCities, setFeaturedCities] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [searchFocused, setSearchFocused] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    // Use demo data as an *initial* render so the page never looks empty,
    // then let a successful backend fetch override it if the API is live.
    setFeaturedProperties(DEMO_PROPERTIES);
    setFeaturedCities(DEMO_CITIES);

    const fetchData = async () => {
      try {
        const [propertiesRes, citiesRes] = await Promise.all([
          fetch(`${API_URL}/api/featured/properties`),
          fetch(`${API_URL}/api/featured/cities`),
        ]);

        if (propertiesRes.ok) {
          const data = await propertiesRes.json();
          if (data.properties?.length) setFeaturedProperties(data.properties);
        }

        if (citiesRes.ok) {
          const data = await citiesRes.json();
          if (data.cities?.length) setFeaturedCities(data.cities);
        }
      } catch (error) {
        // Backend unreachable → we already have demo data seeded above.
        console.warn('Backend unreachable, using demo data:', error.message);
      } finally {
        setLoadingFeatured(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const stats = [
    { label: 'Properties Listed', value: '10,000+' },
    { label: 'Countries Covered', value: '50+' },
    { label: 'Happy Renters', value: '25,000+' },
    { label: 'Verified Owners', value: '5,000+' },
  ];

  const features = [
    {
      icon: MapPin,
      title: 'Exact Location',
      description:
        'See the precise location on the map before you visit. No more hidden addresses.',
    },
    {
      icon: Shield,
      title: 'Verified Owners',
      description:
        'All property owners go through identity verification for your safety.',
    },
    {
      icon: Globe,
      title: 'Global Coverage',
      description:
        'Find properties anywhere in the world with our growing international network.',
    },
    {
      icon: Key,
      title: 'Transparent Pricing',
      description:
        'Connect directly and understand the rental terms before you enquire.',
    },
  ];

  const cityImages = {
    'New York':
      'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?auto=format&fit=crop&w=800&q=80',
    London:
      'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=800&q=80',
    Tokyo:
      'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=800&q=80',
    Paris:
      'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=800&q=80',
    Dubai:
      'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=800&q=80',
    Singapore:
      'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?auto=format&fit=crop&w=800&q=80',
    Sydney:
      'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?auto=format&fit=crop&w=800&q=80',
    Toronto:
      'https://images.unsplash.com/photo-1517935706615-2717063c2225?auto=format&fit=crop&w=800&q=80',
  };

  const faqs = [
    {
      q: 'Is RoomHora free to use?',
      a: 'Yes. RoomHora is free for renters and property owners. We keep the platform accessible while building a trusted rental marketplace.',
    },
    {
      q: 'How does location pinning work?',
      a: 'Property owners can pin the exact property location on the map so renters understand the area before they enquire or visit.',
    },
    {
      q: 'Are property owners verified?',
      a: 'Yes. Owners can complete verification to earn stronger trust signals and help renters identify more reliable listings.',
    },
    {
      q: 'Can I list properties in any country?',
      a: 'Yes. RoomHora is designed for global rental discovery, so listings and search can expand across cities and countries.',
    },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden bg-cream-100" data-testid="landing-page">
      <SEOHead
        title="RoomHora — Find your place, not just a listing"
        description="Map-first rental marketplace with verified owners, transparent pricing, and direct messaging. Search rooms, apartments, houses, and studios worldwide."
        jsonLd={[orgJsonLd(), websiteJsonLd()]}
      />
      <Navbar />

      {/* ------------------------------- HERO ------------------------------- */}
      <RentalNetworkHero
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchFocused={searchFocused}
        setSearchFocused={setSearchFocused}
        handleSearch={handleSearch}
        navigate={navigate}
      />

      {/* ------------------------------- STATS ------------------------------ */}
      <section className="relative z-10 -mt-14 bg-transparent pb-10">
        <div className="rh-container">
          <motion.div
            {...inViewOnce}
            variants={staggerParent(0.1)}
            className="grid grid-cols-2 gap-3 rounded-[1.75rem] border border-white/70 bg-white/90 p-3 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.45)] backdrop-blur-xl md:grid-cols-4 md:gap-4"
          >
            {stats.map((stat) => (
              <motion.div
                key={stat.label}
                variants={fadeUp}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.3, ease: ease.standard }}
                className="rounded-2xl bg-cream-50/75 p-4 text-center ring-1 ring-slate-100/80 transition-shadow duration-300 hover:shadow-soft md:p-5"
              >
                <p className="font-serif text-2xl font-bold text-slate-900 md:text-3xl lg:text-[36px]">
                  <AnimatedCounter value={stat.value} />
                </p>
                <p className="mt-1.5 text-xs font-medium uppercase tracking-[0.08em] text-slate-500 md:text-[13px]">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ----------------------------- FEATURES ----------------------------- */}
      <section className="landing-section-tight bg-cream-50">
        <div className="rh-container">
          <motion.div {...inViewOnce} variants={staggerParent(0.08)} className="mb-8 grid gap-6 lg:grid-cols-[0.78fr_1fr] lg:items-end">
            <div>
              <motion.span
                variants={fadeUpSmall}
                className="mb-3 block text-sm font-medium uppercase tracking-[0.18em] text-accent md:text-base lg:text-base"
              >
                Why RoomHora
              </motion.span>
              <motion.h2
                variants={fadeUp}
                className="font-serif text-3xl text-slate-900 md:text-4xl lg:text-[42px]"
              >
                Rent with the map in sight
              </motion.h2>
            </div>
            <motion.p
              variants={fadeUp}
              className="max-w-2xl text-base leading-7 text-slate-500 md:text-lg lg:ml-auto lg:text-right lg:leading-8"
            >
              Exact location, owner trust, and direct pricing stay visible from
              discovery to enquiry, so renters compare faster and owners get
              cleaner leads.
            </motion.p>
          </motion.div>

          <motion.div
            {...inViewOnce}
            variants={staggerParent(0.08)}
            className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
          >
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                variants={fadeUp}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.35, ease: ease.standard }}
                className="group relative overflow-hidden rounded-2xl border border-cream-300/70 bg-white/70 p-6 transition-all duration-500 hover:border-cream-300 hover:bg-white hover:shadow-luxury"
              >
                {/* Subtle gradient wash on hover */}
                <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-accent/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />

                {/* Puffy/tactile icon: gradient fill + inner highlight + drop
                    shadow so icons read as soft 3D clay, not flat swatches. */}
                <div
                  className="relative mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-accent/90 to-coral-700 shadow-[0_10px_30px_-8px_rgba(244,63,94,0.55),inset_0_1px_0_rgba(255,255,255,0.55)] transition-all duration-500 group-hover:scale-110 group-hover:rotate-[-4deg] group-hover:shadow-glow-accent"
                >
                  <feature.icon className="h-6 w-6 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.25)] transition-colors duration-300" />
                </div>

                <h3 className="relative font-serif text-lg font-bold text-slate-900 md:text-xl lg:text-[20px]">
                  {feature.title}
                </h3>

                <p className="relative mt-3 text-sm leading-6 text-slate-500 md:text-[15px]">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ------------------------------- CITIES ------------------------------ */}
      {featuredCities.length > 0 && (
        <section className="landing-section bg-cream-100">
          <div className="rh-container">
            <motion.div {...inViewOnce} variants={staggerParent(0.08)} className="mb-8 flex items-end justify-between">
              <div>
                <motion.span
                  variants={fadeUpSmall}
                  className="mb-3 block text-sm font-medium uppercase tracking-[0.18em] text-accent md:text-base lg:text-base"
                >
                  Popular Destinations
                </motion.span>
                <motion.h2
                  variants={fadeUp}
                  className="font-serif text-3xl text-slate-900 md:text-4xl lg:text-[42px]"
                >
                  Explore by City
                </motion.h2>
              </div>

              <motion.div variants={fadeUpSmall}>
                <Link
                  to="/search"
                  className="link-underline hidden items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-accent md:flex lg:text-base"
                >
                  View All Cities
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              {...inViewOnce}
              variants={staggerParent(0.06)}
              className="grid gap-5 md:grid-cols-2 lg:grid-cols-3"
            >
              {featuredCities.slice(0, 6).map((city, index) => {
                const count = city.count ?? city.property_count ?? 0;
                return (
                  <motion.div
                    key={city.city}
                    variants={fadeUp}
                    whileHover={{ y: -5 }}
                    transition={{ duration: 0.4, ease: ease.standard }}
                  >
                    <Link
                      to={`/search?city=${encodeURIComponent(city.city)}`}
                      className={`group relative block overflow-hidden rounded-2xl shadow-soft ring-1 ring-slate-200/60 transition-shadow duration-500 hover:shadow-luxury ${
                        index === 0 ? 'h-72 md:h-80 lg:h-[23rem]' : 'h-60 md:h-64 lg:h-[23rem]'
                      }`}
                      data-testid={`city-${city.city.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <img
                        src={
                          cityImages[city.city] ||
                          `https://source.unsplash.com/800x600/?${city.city},city`
                        }
                        alt={city.city}
                        className="h-full w-full object-cover transition-transform [transition-duration:1100ms] ease-luxury group-hover:scale-[1.12]"
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-opacity duration-500 group-hover:from-black/90" />

                      <div className="absolute left-5 top-5 rounded-full border border-white/25 bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-white/85 backdrop-blur">
                        {city.country}
                      </div>

                      <div className="absolute inset-x-0 bottom-0 p-6 transition-transform duration-500 group-hover:-translate-y-1">
                        <h3 className="font-serif text-2xl font-bold text-white lg:text-3xl">
                          {city.city}
                        </h3>
                        <p className="mt-1 flex items-center gap-2 text-sm text-white/85 lg:text-base">
                          {count.toLocaleString()} {count === 1 ? 'property' : 'properties'}
                          <ArrowRight className="h-3.5 w-3.5 translate-x-0 opacity-0 transition-all duration-500 group-hover:translate-x-1 group-hover:opacity-100" />
                        </p>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>
      )}

      {/* ------------------------- FEATURED PROPERTIES ----------------------- */}
      <section className="landing-section bg-cream-50">
        <div className="rh-container">
          <motion.div {...inViewOnce} variants={staggerParent(0.08)} className="mb-8 flex items-end justify-between">
            <div>
              <motion.span
                variants={fadeUpSmall}
                className="mb-3 block text-sm font-medium uppercase tracking-[0.18em] text-accent md:text-base lg:text-base"
              >
                Featured Listings
              </motion.span>
              <motion.h2
                variants={fadeUp}
                className="font-serif text-3xl text-slate-900 md:text-4xl lg:text-[42px]"
              >
                Popular Properties
              </motion.h2>
            </div>

            <motion.div variants={fadeUpSmall}>
              <Link
                to="/search"
                className="link-underline hidden items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-accent md:flex lg:text-base"
              >
                View All Properties
                <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            {...inViewOnce}
            variants={staggerParent(0.08)}
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {loadingFeatured ? (
              <PropertyCardSkeleton count={6} />
            ) : (
              featuredProperties.map((property) => (
                <motion.div key={property.property_id} variants={fadeUp}>
                  <PropertyCard property={property} />
                </motion.div>
              ))
            )}
          </motion.div>
        </div>
      </section>

      {/* --------------------------- OWNER CTA ------------------------------ */}
      <section className="landing-section relative overflow-hidden bg-slate-900 text-white">
        {/* soft radial light */}
        <div className="pointer-events-none absolute inset-0 bg-radial-fade" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

        <div className="rh-container relative">
          <div className="grid items-center gap-10 lg:grid-cols-[0.86fr_1.14fr]">
            <motion.div {...inViewOnce} variants={staggerParent(0.08)}>
              <motion.span
                variants={fadeUpSmall}
                className="mb-4 block text-sm font-medium uppercase tracking-[0.18em] text-accent md:text-base lg:text-base"
              >
                Property Owners
              </motion.span>

              <motion.h2
                variants={fadeUp}
                className="font-serif text-3xl md:text-4xl lg:text-[42px] lg:leading-tight"
              >
                List Your Property for Free
              </motion.h2>

              <motion.p
                variants={fadeUp}
                className="mt-5 max-w-xl text-base leading-7 text-slate-300 md:text-lg lg:leading-8"
              >
                Join owners who connect directly with renters. No commission
                fees, no middlemen — just clear, transparent rentals.
              </motion.p>

              <motion.ul
                variants={staggerParent(0.08)}
                className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-1"
              >
                {[
                  'Free listing with unlimited photos and videos',
                  'Exact location pinning on interactive map',
                  'Direct messages from verified renters',
                  'Dashboard to manage all your properties',
                ].map((item, i) => (
                  <motion.li key={i} variants={fadeUpSmall} className="flex items-center gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/20">
                      <CheckCircle2 className="h-4 w-4 text-accent" />
                    </span>
                    <span className="text-sm text-slate-300 md:text-base lg:text-base">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>

              <motion.div variants={fadeUp} className="mt-7 flex flex-wrap gap-3">
                <motion.div whileHover={{ scale: 1.03, y: -1 }} whileTap={{ scale: 0.97 }} transition={{ duration: 0.2 }}>
                  <Button
                    onClick={() => navigate('/login')}
                    className="btn-shine rounded-full bg-accent px-8 py-6 text-base font-medium hover:bg-accent/90 md:text-lg lg:text-base"
                    data-testid="cta-list-property"
                  >
                    Start Listing Today
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.03, y: -1 }} whileTap={{ scale: 0.97 }} transition={{ duration: 0.2 }}>
                  <Button
                    onClick={() => navigate('/search')}
                    variant="outline"
                    className="rounded-full border-white/25 bg-white/5 px-8 py-6 text-base font-medium text-white backdrop-blur hover:border-white/50 hover:bg-white/10 md:text-lg lg:text-base"
                  >
                    Explore first
                  </Button>
                </motion.div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 36 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, ease: ease.gentle }}
              className="relative"
            >
              <div className="overflow-hidden rounded-3xl shadow-2xl ring-1 ring-white/10">
                <LivingRoomHoraScene variant="panel" className="min-h-[380px] lg:min-h-[430px]" />
              </div>

              {/* Floating rating card */}
              <motion.div
                initial={{ opacity: 0, y: 18, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.6, delay: 0.3, ease: ease.gentle }}
                whileHover={{ y: -4 }}
                className="absolute -bottom-6 left-4 rounded-2xl bg-white p-5 shadow-luxury md:-left-6"
              >
                <motion.div
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                  className="flex items-center gap-4"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10">
                    <Star className="h-6 w-6 fill-accent text-accent" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-900">
                      <AnimatedCounter value="4.9" /> Rating
                    </p>
                    <p className="text-sm text-slate-500">
                      From <AnimatedCounter value="10,000+" /> reviews
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --------------------------------- FAQ ------------------------------ */}
      <section className="landing-section-tight bg-cream-50">
        <div className="rh-container grid gap-8 lg:grid-cols-[0.72fr_1.28fr]">
          <motion.div {...inViewOnce} variants={staggerParent(0.08)} className="lg:pt-3">
            <motion.span
              variants={fadeUpSmall}
              className="mb-3 block text-sm font-medium uppercase tracking-[0.18em] text-accent md:text-base lg:text-base"
            >
              FAQ
            </motion.span>
            <motion.h2
              variants={fadeUp}
              className="font-serif text-3xl text-slate-900 md:text-4xl lg:text-[42px]"
            >
              Common Questions
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="mt-4 max-w-md text-base leading-7 text-slate-500"
            >
              Quick answers for renters and owners before they jump into the
              map, listing flow, or owner dashboard.
            </motion.p>
          </motion.div>

          <motion.div {...inViewOnce} variants={staggerParent(0.06)} className="space-y-3">
            {faqs.map((faq, i) => {
              const open = openFaq === i;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="overflow-hidden rounded-2xl border border-slate-100 bg-white/80 shadow-sm transition-shadow duration-300 hover:shadow-md"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left md:px-7"
                    aria-expanded={open}
                  >
                    <h3 className="font-serif text-base font-bold text-slate-900 md:text-lg lg:text-xl">
                      {faq.q}
                    </h3>
                    <motion.span
                      animate={{ rotate: open ? 180 : 0 }}
                      transition={{ duration: 0.3, ease: ease.standard }}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: ease.standard }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-5 text-sm leading-7 text-slate-700 md:px-7 md:text-base">
                          {faq.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
